import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { ReactComponent as ErrorIcon } from "../../../media/icons/error.svg";
import { ReactComponent as SuccessIcon } from "../../../media/icons/notification-success.svg";
import { ReactComponent as CloseIcon } from "../../../media/icons/close-grey.svg";
import { getSafeErrorText } from "../../../helpers/safeRender";
import styles from "./notification.module.css";
import { useToastOffset } from "./toastStack";

const ToastNotification = ({ children, error, success, time, close, timeToShow }) => {
  const { t } = useTranslation();
  const [isShown, setIsShown] = useState(null);
  const toastRef = useRef(null);
  const offset = useToastOffset(toastRef, isShown !== false);

  useEffect(() => {
    const timeoutShow = setTimeout(() => setIsShown(true), timeToShow || 200);
    const timeoutHide = setTimeout(() => {
      if (close) {
        close();
      }
      setIsShown(false);
    }, time || 7000);

    return () => {
      clearTimeout(timeoutShow);
      clearTimeout(timeoutHide);
    };
  }, []); //eslint-disable-line

  const handleClose = () => {
    if (close) {
      close();
    }
    setIsShown(false);
  };

  return (
    <div ref={toastRef} className={cn(styles.feedback, isShown ? styles.shown : null)} style={{ "--toast-offset": `${offset}px` }}>
      {error && <ErrorIcon className={styles.icon} />}
      {success && <SuccessIcon className={styles.icon} />} {getSafeErrorText(children)}
      <button type="button" onClick={handleClose} className={styles.closeIcon} aria-label={t("dashboard_common_close")}>
        <CloseIcon />
      </button>
    </div>
  );
};

const InlineNotification = ({ children, error, success, time, close, dismissible = true }) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    // In a form taller than the screen (Add Card at phone width) the message would appear above the submit button the
    // member just pressed, out of view. `nearest` leaves a message that is already visible where it is.
    messageRef.current?.scrollIntoView?.({ block: "nearest" });
  }, []);

  useEffect(() => {
    // The toast always hides itself; an inline message stays until it is dismissed or its owner removes it, unless it
    // is given a `time`.
    if (!time) {
      return undefined;
    }

    const timeoutHide = setTimeout(() => {
      if (close) {
        close();
      }
      setIsDismissed(true);
    }, time);

    return () => clearTimeout(timeoutHide);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- armed once, at mount, from the props the message first renders with

  const handleClose = () => {
    if (close) {
      close();
    }
    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div ref={messageRef} role="alert" className={cn(styles.feedback, styles.inline, error ? styles.inlineError : null)}>
      {error && <ErrorIcon className={styles.icon} />}
      {success && <SuccessIcon className={styles.icon} />}
      <span className={styles.inlineText}>{getSafeErrorText(children)}</span>
      {dismissible && (
        <button type="button" onClick={handleClose} className={styles.closeIcon} aria-label={t("dashboard_common_close")}>
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

/**
 * `inline` renders the message in normal flow (full width, wrapping, announced as an alert) instead of as a fixed
 * top-right toast. Use it inside a modal: from 768px the shared Modal is moved with a `transform`, which pins a fixed
 * toast to the modal box instead of the screen. `dismissible={false}` (inline only) leaves out the ✕, for a message that
 * has to stay as long as its cause does.
 */
export const Notification = ({ inline, ...props }) => (inline ? <InlineNotification {...props} /> : <ToastNotification {...props} />);

export const NotificationListing = ({ children, error, success, time, onClose }) => {
  const { t } = useTranslation();
  const [isShown, setIsShown] = useState(null);
  const toastRef = useRef(null);
  const offset = useToastOffset(toastRef, isShown !== false);

  useEffect(() => {
    if (!error && !success) {
      return undefined;
    }

    const timeoutShow = setTimeout(() => setIsShown(true), 200);
    const timeoutHide = setTimeout(() => {
      setIsShown(false);
      onClose();
    }, time || 7000);

    return () => {
      clearTimeout(timeoutShow);
      clearTimeout(timeoutHide);
    };
  }, [error, success]); //eslint-disable-line

  return (
    <div ref={toastRef} className={cn(styles.feedback, isShown ? styles.shown : null)} style={{ "--toast-offset": `${offset}px` }}>
      {error && <ErrorIcon className={styles.icon} />}
      {success && <SuccessIcon className={styles.icon} />} {getSafeErrorText(children)}
      <button
        type="button"
        onClick={() => {
          setIsShown(false);
          onClose();
        }}
        className={styles.closeIcon}
        aria-label={t("dashboard_common_close")}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

export const NotificationError = ({ text, time, onClose }) => {
  const { t } = useTranslation();
  const toastRef = useRef(null);
  const offset = useToastOffset(toastRef, true);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onClose();
    }, time || 6000);

    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, []); //eslint-disable-line

  return (
    <div ref={toastRef} className={cn(styles.feedback, styles.shown)} style={{ "--toast-offset": `${offset}px` }}>
      <ErrorIcon className={styles.icon} /> {text || t("dashboard_common_error_generic")}
      <button type="button" onClick={onClose} className={styles.closeIcon} aria-label={t("dashboard_common_close")}>
        <CloseIcon />
      </button>
    </div>
  );
};
